/**
 * Modal component — Brand Book §2.4
 * 480px (default), 640px (wide), 0px radius
 * Overlay rgba(0,0,0,0.5), card translateY(8px→0) animation
 * Focus trap, Escape closes, full-screen on mobile (<640px)
 * Portal-rendered to document.body
 */

import { useEffect, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { X } from 'react-feather';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  footer?: ReactNode;
  className?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  footer,
  className,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Stable close handler
  const handleClose = useCallback(() => onClose(), [onClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) {return;}

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {handleClose();}
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) {return;}

    previousFocusRef.current = document.activeElement as HTMLElement;

    const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    firstFocusable?.focus();

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') {return;}

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    return () => {
      document.removeEventListener('keydown', handleTab);
      previousFocusRef.current?.focus();
    };
  }, [isOpen]);

  if (!isOpen) {return null;}

  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-[480px]',
    lg: 'max-w-[640px]',
    xl: 'max-w-2xl',
    // Landscape frame for the deal wizard (~800px content inside the p-4 body,
    // matching the 800×267 mockup the two-pane layout was designed for).
    '2xl': 'max-w-[832px]',
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50"
        style={{ animation: 'modalFadeIn 200ms ease' }}
        onClick={closeOnOverlayClick ? handleClose : undefined}
        aria-hidden="true"
      />

      {/* Modal Card */}
      <div
        ref={modalRef}
        className={clsx(
          'relative w-full bg-surface-card border border-border shadow-lg',
          'max-sm:fixed max-sm:inset-0 max-sm:max-w-none',
          sizes[size],
          className
        )}
        style={{ animation: 'modalEnter 200ms ease' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-4 py-4 border-b border-border">
            {title && (
              <h2 id="modal-title" className="text-h2 text-txt-primary">{title}</h2>
            )}
            {showCloseButton && (
              <button
                onClick={handleClose}
                className={clsx(
                  'p-1 text-txt-tertiary transition-colors duration-200',
                  'hover:text-txt-primary hover:bg-surface-subtle',
                  !title && 'ml-auto'
                )}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="p-4 overflow-y-auto max-h-[calc(100vh-200px)] custom-scrollbar">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 px-4 py-4 border-t border-border">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}